/* Copyright (c) 2024 Xperi. All Rights Reserved. */

import React, { FunctionComponent, useEffect } from 'react';

import { Locale, LocaleKeys } from '../../../../core/locale';
import { CursorManager, KeyManager } from '../../../../core/navigation';
import {
    RootState,
    useTivoDispatch,
    useTivoSelector,
} from '../../../../core/tivo/tivo-redux-wrapper';
import { setHideAdultContentSettingVerticalLimitAction } from '../../../../store/settings/hideadultcontent/hideadultcontent.actions';
import { onCursorStateChangeHideAdultContentOperations } from '../../../../store/settings/hideadultcontent/hideadultcontent.reducers';
import { ClockTypes } from '../../../Clock';
import { AntiburnOsd, ScreenHeader, ScreenPreview } from '../../../common';
import { Menu } from '../../Menu';

import './HideAdultContent.scss';

const HideAdultContent: FunctionComponent = (): JSX.Element => {
    const dispatch = useTivoDispatch();

    const { verticalPosition, hideAdultContent, isFocused } = useTivoSelector(
        (state: RootState) => state.hideAdultContent,
    );
    const { isActive: isAntiburnOsdActive } = useTivoSelector(
        (state: RootState) => state.antiburnOsd,
    );

    const hideAdultContentOptions = [
        Locale.getString(LocaleKeys.SETTINGS_PC_HIDE_ADULT_COTENT_TEXT),
        Locale.getString(LocaleKeys.SETTINGS_PC_HIDE_ADULT_CONTENT_DONT_HIDE_TEXT),
    ];

    const onCursorStateChangeCallback = () => {
        dispatch(onCursorStateChangeHideAdultContentOperations());
    };

    useEffect(() => {
        const refCursorManager = CursorManager.subscribe(onCursorStateChangeCallback);
        const keyManagerIns: KeyManager = KeyManager.getInstance();
        if (keyManagerIns.isKeyIgnoring()) {
            keyManagerIns.stopKeyIgnoring();
        }
        return () => {
            CursorManager.unsubscribe(refCursorManager);
        };
    }, []);

    useEffect(() => {
        dispatch(setHideAdultContentSettingVerticalLimitAction(hideAdultContentOptions.length));
    }, []);

    const screenPreviewComponent = () => {
        return (
            <ScreenPreview classStyle="preview">
                {verticalPosition === 0 ? (
                    <React.Fragment>
                        <div className="lock-image"></div>
                        <p>
                            {Locale.getString(
                                LocaleKeys.SETTINGS_PC_HIDE_ADULT_CONTENT_PREVIEW_TEXT,
                            )}
                        </p>
                    </React.Fragment>
                ) : (
                    <div>{Locale.getString(LocaleKeys.SETTINGS_PC_DONT_HIDE_PREVIEW_TEXT)}</div>
                )}
            </ScreenPreview>
        );
    };

    return (
        <div className="hide-adult-content-container">
            <ScreenHeader
                title={Locale.getString(
                    LocaleKeys.SETTINGS_PC_HIDE_ADULT_CONTENT_HEADER_TITLE_TEXT,
                )}
                clockType={ClockTypes.DEFAULT_CLOCK}
            />
            <div className="hide-adult-content-body">
                <div className="hide-adult-content-description">
                    {Locale.getString(LocaleKeys.SETTINGS_PC_HIDE_ADULT_CONTENT_DESCRIPTION)}
                </div>
                <div className="hide-adult-content-menu">
                    <Menu
                        items={hideAdultContentOptions}
                        focusedIndex={verticalPosition}
                        isFocused={isFocused}
                        tick={Number(!hideAdultContent)}
                        selectedIndex={verticalPosition}
                    />
                </div>
            </div>
            {screenPreviewComponent()}
            {isAntiburnOsdActive && <AntiburnOsd />}
        </div>
    );
};

export default HideAdultContent;
